import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Minus, Plus, Trash2, ShoppingBag, MessageCircle, Loader2, CheckCircle2 } from "lucide-react";
import { useCart } from "../context/CartContext";
import { STORE, waLink, formatGs } from "../data/store";
import { api, assetUrl } from "../lib/api";

export const buildOrderMessage = (items, total, name, notes, number) => {
  const lines = items.map((i, idx) =>
    `${idx + 1}. [${i.code}] ${i.name} x${i.qty} (${i.unit}) — ${formatGs(i.price * i.qty)}`);
  const head = number ? `Hola ${STORE.name}, les envío mi pedido #${number}:` : `Hola ${STORE.name}, quiero hacer el siguiente pedido:`;
  let msg = `${head}\n\nNombre: ${name}\n\nProductos:\n${lines.join("\n")}\n\nTotal estimado: ${formatGs(total)}`;
  if (notes.trim()) msg += `\n\nObservaciones: ${notes.trim()}`;
  msg += "\n\n¿Me confirman disponibilidad y forma de entrega?";
  return msg;
};

export const CartDrawer = () => {
  const { items, setQty, remove, clear, total, count, open, setOpen } = useCart();
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const canSend = items.length > 0 && name.trim().length >= 2 && !sending;

  const send = async () => {
    if (!canSend) return;
    setSending(true);
    const win = window.open("about:blank", "_blank");
    let number = null;
    try {
      const order = await api.createOrder({
        customer_name: name.trim(), phone: phone.trim(), notes: notes.trim(),
        items: items.map((i) => ({ code: i.code, name: i.name, price: i.price, qty: i.qty, unit: i.unit || "" })),
      });
      number = order.number;
    } catch { /* still send via WhatsApp */ }
    const url = waLink(buildOrderMessage(items, total, name.trim(), notes, number));
    if (win) win.location.href = url; else window.open(url, "_blank");
    setSent({ number, total, count });
    clear(); setNotes("");
    setSending(false);
  };

  const close = () => { setOpen(false); setTimeout(() => setSent(null), 300); };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/50" onClick={close}
            data-testid="cart-overlay"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.25 }}
            className="fixed top-0 right-0 z-[70] h-full w-full max-w-md bg-white shadow-2xl flex flex-col"
            data-testid="cart-drawer"
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between bg-[#0A3977] text-white">
              <div className="flex items-center gap-2 font-display font-bold text-lg">
                <ShoppingBag className="w-5 h-5" /> Tu pedido
                <span data-testid="cart-count-label" className="ml-1 text-xs font-bold bg-[#E02424] rounded-full px-2 py-0.5">{count}</span>
              </div>
              <button data-testid="cart-close" onClick={close} className="p-1 rounded hover:bg-white/10"><X className="w-5 h-5" /></button>
            </div>

            {sent ? (
              <div className="flex-1 flex flex-col items-center justify-center text-center px-8" data-testid="order-sent">
                <CheckCircle2 className="w-16 h-16 text-[#25D366] mb-4" />
                <h3 className="font-display font-bold text-2xl text-gray-900">¡Pedido enviado!</h3>
                {sent.number && <p className="mt-1 font-mono-code text-sm font-bold text-[#0A3977]" data-testid="order-number">Pedido #{sent.number}</p>}
                <p className="mt-3 text-gray-500 text-sm">
                  Se abrió WhatsApp con el detalle de tu pedido ({sent.count} {sent.count === 1 ? "producto" : "productos"}, {formatGs(sent.total)}).
                  Te responderemos a la brevedad.
                </p>
                <button data-testid="order-sent-close" onClick={close} className="mt-6 px-6 py-3 rounded-full bg-[#0A3977] text-white font-bold">Seguir viendo productos</button>
              </div>
            ) : (
              <>
                <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3">
                  {items.length === 0 ? (
                    <div className="text-center py-16 text-gray-400" data-testid="cart-empty">
                      <ShoppingBag className="w-10 h-10 mx-auto mb-3" />
                      <p className="font-semibold">Tu pedido está vacío</p>
                      <p className="text-sm mt-1">Agregá productos desde el catálogo.</p>
                    </div>
                  ) : items.map((i) => (
                    <div key={i.id} data-testid={`cart-item-${i.code}`} className="flex gap-3 border border-gray-200 rounded-xl p-3">
                      <img src={assetUrl(i.image_url)} alt={i.name} className="w-16 h-16 rounded-lg object-cover bg-gray-100 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-mono-code text-[10px] font-bold text-gray-400">{i.code}</div>
                        <div className="font-semibold text-sm text-gray-900 line-clamp-2 leading-snug">{i.name}</div>
                        <div className="text-xs text-gray-500">{formatGs(i.price)} · {i.unit}</div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="inline-flex items-center border border-gray-300 rounded-full">
                            <button data-testid={`cart-dec-${i.code}`} onClick={() => setQty(i.id, i.qty - 1)} className="p-1.5 hover:bg-gray-100 rounded-l-full"><Minus className="w-3.5 h-3.5" /></button>
                            <span data-testid={`cart-qty-${i.code}`} className="w-8 text-center text-sm font-bold">{i.qty}</span>
                            <button data-testid={`cart-inc-${i.code}`} onClick={() => setQty(i.id, i.qty + 1)} className="p-1.5 hover:bg-gray-100 rounded-r-full"><Plus className="w-3.5 h-3.5" /></button>
                          </div>
                          <div className="font-bold text-sm text-[#E02424]">{formatGs(i.price * i.qty)}</div>
                        </div>
                      </div>
                      <button data-testid={`cart-remove-${i.code}`} onClick={() => remove(i.id)} className="self-start p-1.5 text-gray-400 hover:text-[#E02424]"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  ))}
                </div>

                <div className="border-t border-gray-200 px-5 py-4 space-y-3 bg-[#FAFAFA]">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold text-gray-600">Total estimado</span>
                    <span data-testid="cart-total" className="font-display font-black text-2xl text-[#E02424]">{formatGs(total)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      data-testid="cart-name-input" value={name} onChange={(e) => setName(e.target.value)}
                      placeholder="Tu nombre *"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white"
                    />
                    <input
                      data-testid="cart-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)}
                      placeholder="Tu teléfono (opcional)" inputMode="tel"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white"
                    />
                  </div>
                  <textarea
                    data-testid="cart-notes-input" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                    placeholder="Observaciones (dirección, color, forma de entrega...)"
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg outline-none focus:border-[#0067B1] text-sm bg-white resize-none"
                  />
                  <button
                    data-testid="cart-send-whatsapp" onClick={send} disabled={!canSend}
                    className={`w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-bold transition-colors ${
                      canSend ? "bg-[#25D366] text-white hover:bg-[#1EBE5B]" : "bg-gray-200 text-gray-400 cursor-not-allowed"}`}
                  >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Enviar pedido por WhatsApp
                  </button>
                  {items.length > 0 && name.trim().length < 2 && (
                    <p className="text-xs text-center text-gray-500">Ingresá tu nombre para enviar el pedido.</p>
                  )}
                  {items.length > 0 && (
                    <button data-testid="cart-clear" onClick={clear} className="w-full text-xs font-semibold text-gray-500 hover:text-[#E02424]">Vaciar pedido</button>
                  )}
                </div>
              </>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};
